// Top-of-screen kingdom strip. After Phase 5.1 the tabs no longer switch the
// playable kingdom — only the player's home kingdom is playable. Clicking a
// non-home tab opens the kingdom view modal (read-only stats + defect option).

import { Events, on, emit } from './eventBus.js'
import { KingdomOrder, KingdomConfig, CatColors } from '../config/balance.js'
import { getProfile } from '../game/state.js'

const FORMATTER = new Intl.NumberFormat('en-US')

export function mountKingdomTabs({ root }) {
  // Three-part top bar: left = citizenship (plain text), center = kingdom
  // tabs, right = player MAO name + profile circle.
  root.innerHTML = `
    <div class="hud__citizen" data-federation>—</div>
    <div class="hud__kingdom-tabs">${KingdomOrder.map(tabTemplate).join('')}</div>
    <div class="hud__profile" data-profile title="Your registry">
      <span class="hud__profile-name" data-profile-name>—</span>
      <span class="hud__profile-pic" data-profile-pic></span>
    </div>
  `

  // Profile chip → open the player's own Registry.
  const profileEl = root.querySelector('[data-profile]')
  profileEl.addEventListener('click', () => emit(Events.RegistryOpen, { target: null }))
  const nameEl = root.querySelector('[data-profile-name]')
  const picEl = root.querySelector('[data-profile-pic]')

  const tabs = new Map()
  for (const id of KingdomOrder) {
    const el = root.querySelector(`[data-kingdom="${id}"]`)
    tabs.set(id, el)
    el.addEventListener('click', () => {
      // Always opens the view modal — even for the home kingdom (it just shows
      // your own stats with no defect button). Switching kingdoms via tab click
      // is disallowed.
      emit(Events.KingdomViewRequested, { kingdom: id })
    })
  }

  const fedEl = root.querySelector('[data-federation]')
  const refresh = () => {
    const profile = getProfile()
    const homeId = profile.homeKingdom
    for (const id of KingdomOrder) {
      const el = tabs.get(id)
      el.classList.toggle('active', id === homeId)
      el.classList.remove('locked', 'can-unlock')
      const cost = el.querySelector('[data-cost]')
      if (cost) cost.textContent = id === homeId ? 'HOME' : 'view'
    }
    if (homeId) {
      fedEl.textContent = `Citizen of ${KingdomConfig[homeId].name} ${KingdomConfig[homeId].subtitle}`
      fedEl.classList.add('active')
    } else {
      fedEl.textContent = 'Choose your kingdom'
      fedEl.classList.remove('active')
    }
    // Profile chip — MAO name + a coat-colored circle with the name's initial.
    const name = profile.catName || '—'
    nameEl.textContent = name
    const coat = (CatColors.find((c) => c.id === profile.catColorId) ?? CatColors[0]).body
    const hex = '#' + coat.toString(16).padStart(6, '0')
    picEl.style.background = hex
    picEl.style.color = contrastText(coat)
    picEl.textContent = (name === '—' ? '?' : name).slice(0, 1).toUpperCase()
  }

  on(Events.ProfileChange, refresh)
  on(Events.KingdomChange, refresh)
  refresh()
}

function tabTemplate(id) {
  const cfg = KingdomConfig[id]
  return `
    <button class="hud__kingdom-tab" data-kingdom="${id}" style="--accent:${cfg.accent}">
      <span class="name">${cfg.name}</span>
      <span class="sub">${cfg.subtitle}</span>
      <span class="cost" data-cost></span>
    </button>
  `
}

// Pick black or white text for legibility over a given fill color (per
// relative luminance). `hex` is a 0xRRGGBB number.
function contrastText(hex) {
  const r = (hex >> 16) & 0xff
  const g = (hex >> 8) & 0xff
  const b = hex & 0xff
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return lum > 0.6 ? '#1a1a1a' : '#f4ead5'
}

// `FORMATTER` retained for potential cost displays in a future variant.
void FORMATTER
